import { get } from 'lodash';

import { CopyType } from '@/const/lits';
import { postCondition } from '@/editor/flowEditor/control/utils/changeCondition';
import { postGamePlay } from '@/editor/flowEditor/control/utils/gamePlay';
import { eventPool } from '@/event/eventPool';
import { IConditionFlow, IScene } from '@/interface';
import { IGamePlay } from '@/interface/gamePlay';
import { postMaterial } from '@/utils/material';
import { transformObjectDeep } from '@/utils/tans/object';

import { apiPathServer } from './api-config';
import { CgiResp } from './cgi';
import { API_PREFIX, API_PREFIX_PLOT, request } from './request';

/**
 * 保存Scene信息
 */
export const modifySceneDetail = async (
  scene: Partial<IScene>,
  extendInfo: {
    force?: boolean, close?: boolean, pageUniqueKey?: string
  },
) => {
  const { intimacy, materialMap, ...postData }: any = scene || {};

  // -- start TODO 确认字段是否使用
  const { initScore, minScore, maxScore } = intimacy || {};
  postData.intimacy = initScore || minScore || maxScore
    ? intimacy
    : { ...intimacy, initScore: 0, minScore: 0, maxScore: 0 };
  // -- end

  postData.materialMap = postMaterial(postData, materialMap);

  const res = await request(`${API_PREFIX}${apiPathServer.postScene}`, {
    method: 'POST',
    body: transformObjectDeep({
      ...extendInfo,
      data: postData,
    }, 'snake'),
  });
  return transformObjectDeep(res, 'camel') as CgiResp<IScene & { id: string }>;
};

export const deleteScene = async (id: string) => {
  const res = await request(`${API_PREFIX}${apiPathServer.deleteScene}`, {
    method: 'POST',
    body: { id },
  });
  return res;
};

/**
 * copy项目信息
 */
export const copyScene = async (id: string) => {
  const res = await request(`${API_PREFIX}${apiPathServer.copy}`, {
    method: 'POST',
    body: { copy_id: id, type: CopyType.SCENE },
  });
  return res;
};

/**
 * 拉取章节详情
 */
export const fetchSceneDetail = async ({ id = '', release = false, close = false }) => {
  const res = await request<CgiResp<any>>(
    `${API_PREFIX}${apiPathServer.sceneDetail}${release ? '/release' : ''}`,
    {
      query: {
        id,
        depth: 3,
        close,
      },
    },
  );
  return transformObjectDeep(res, 'camel') as CgiResp;
};

/**
 *  拉取列表【已废弃】
 * @param param0
 * @returns
 */
export const fetchSceneList = async ({
  page = 1,
  size = 10,
  sceneIds = [],
}: {
  page?: number;
  size?: number;
  sceneIds?: string[];
}) => {
  const res = await request(`${API_PREFIX}${apiPathServer.sceneList}`, {
    sceneIds,
  });
  const payload = get(res, 'data', { list: [], total: 0, page, size });
  payload.list.forEach((ret: any) => {
    const tmpValue = ret;
    tmpValue.actions = JSON.parse(ret.actions || '[]');
  });
  return payload;
};

/**
 * 获取故事的所有幕
 */
export const getStoryFullSceneList = async ({
  id = '',
  release = false,
  page = 1,
  size = 999,
}) => {
  const res = await request(`${API_PREFIX}${apiPathServer.fullSceneList}${release ? '/release' : ''}`, {
    query: {
      page,
      size,
      story_id: id,
    },
  });
  const { list = [] } = res?.data || {};
  return {
    code: res?.code,
    message: res?.message,
    list: list?.map((item: any) => transformObjectDeep(item, 'camel')) || [],
  };
};

/**
 * 更新Scene条件
 */
export const updateConditions = async (
  sceneId: string,
  conditions: IConditionFlow[],
) => {
  const passConditions = postCondition(conditions);
  const postData = { sceneId, passConditions };
  const res = await request(
    `${API_PREFIX_PLOT}${apiPathServer.updateCondtions}`,
    {
      method: 'POST',
      body: transformObjectDeep(postData, 'snake'),
    },
  );
  eventPool.eventsEmit.refreshStoryline();
  return res;
};

/**
 * 更新初始幕
 */
export const updateFirstScene = async (sceneId: string) => {
  const postData = { sceneId };
  const res = await request(
    `${API_PREFIX_PLOT}${apiPathServer.updateFirstScene}`,
    {
      method: 'POST',
      body: transformObjectDeep(postData, 'snake'),
    },
  );
  return res;
};

/**
 * 清除数值系统
 */
export const deleteIntimacy = async (sceneId: string) => {
  const postData = { sceneId };
  const res = await request(
    `${API_PREFIX_PLOT}${apiPathServer.deleteIntimacy}`,
    {
      method: 'POST',
      body: transformObjectDeep(postData, 'snake'),
    },
  );
  return res;
};

/**
 * 获取特殊玩法
 */
export const getGamePlay = async (id: string) => {
  const res = await request<CgiResp<any>>(
    `${API_PREFIX}${apiPathServer.gamePlayDetail}`,
    {
      query: { id },
    },
  );
  return transformObjectDeep(res, 'camel') as CgiResp;
};

/**
 * 更新特殊玩法
 */
export const updateGamePlay = async (
  sceneId: string,
  storyId: string,
  gamePlay: IGamePlay,
) => {
  const aiGamePlay = postGamePlay(gamePlay);
  const postData = {
    id: sceneId,
    storyId,
    aiGamePlay,
  };
  const res = await request(`${API_PREFIX}${apiPathServer.updateGamePlay}`, {
    method: 'POST',
    body: transformObjectDeep(postData, 'snake'),
  });
  return res;
};
